/*
 * @Author: Yumeng Xue
 * @Date: 2025-12-27 00:12:39
 * @LastEditTime: 2025-12-30 22:30:52
 * @LastEditors: Yumeng Xue
 * @Description: 
 * @FilePath: /topo-mess/src/core/structure-tensor-scanner.ts
 */
import { Binning, LineData } from "./binning";

export interface Tensor2x2 {
    xx: number;
    xy: number;
    yy: number;
}

export interface StructureTensorScanResult {
    coherenceScores: number[][]; // 相干度 (0=无序, 1=有序)
    densityScores: number[][]; // 密度
    sampleCounts: number[][];
    // 可选：如果需要在前端做更细粒度的交互，可以返回原始的前缀和数据
    // lineIntegralArrays?: Float32Array[];
}

export interface LineFieldProfileStats {
    mean: number;
    variance: number;
    std: number;
    highCoverage: number;
}

interface SparseLineSelfContributionField {
    binIds: Int32Array;
    xx: Float32Array;
    xy: Float32Array;
    yy: Float32Array;
    trace: Float32Array;
}

interface KernelSupport {
    indices: Int32Array;
    weights: Float32Array;
}

export class StructureTensorScanner {
    private width: number;
    private height: number;
    private tensorField: Tensor2x2[][];
    private densityField: number[][]; // 加权密度场 (bin.density = 像素穿越总数)

    // 存储每条线的前缀和数组。
    // key: lineIndex, value: cumulative energy array [E_0, E_0+E_1, ...]
    private lineIntegralEnergies: Float32Array[] = [];

    private lineTraces: Int32Array[] = []; // 每条线的路径坐标数组 [x0, y0, x1, y1, ...]

    private smoothDensityField: number[][] = [];
    private gaussianKernel: Float32Array = new Float32Array([1]);
    private xKernelSupports: KernelSupport[] = [];
    private yKernelSupports: KernelSupport[] = [];
    private lineSelfContributionFields: SparseLineSelfContributionField[] = [];

    constructor(width: number, height: number) {
        this.width = width;
        this.height = height;
        this.tensorField = [];
        this.densityField = [];
    }

    /**
     * 核心流程：初始化 -> 平滑 -> 积分
     * @param sigma 高斯平滑半径 (标准差), 推荐 1.0 - 3.0
     */
    public initField(binning: Binning, lines: LineData[], sigma: number, LOO = false) {
        const bins = binning.getBins();
        const [w, h] = binning.getBinCounts();


        // 1. Eulerian Phase: Rasterize Raw Tensor Field
        // -------------------------------------------------
        const rawJxx = new Float32Array(w * h).fill(0);
        const rawJxy = new Float32Array(w * h).fill(0);
        const rawJyy = new Float32Array(w * h).fill(0);
        const rawDensity = new Float32Array(w * h).fill(0);
        const gaussianKernel = sigma > 0 ? this.makeGaussianKernel(sigma) : [1];
        this.gaussianKernel = Float32Array.from(gaussianKernel);
        this.xKernelSupports = this.buildKernelSupports(w, this.gaussianKernel);
        this.yKernelSupports = this.buildKernelSupports(h, this.gaussianKernel);
        this.lineSelfContributionFields = LOO
            ? this.cacheLineSelfContributions(bins, lines.length, w)
            : [];

        // 遍历每个bin，累加结构张量和密度
        for (let x = 0; x < w; x++) {
            for (let y = 0; y < h; y++) {
                const bin = bins[x][y];

                let rawBinJxx = 0;
                let rawBinJxy = 0;
                let rawBinJyy = 0;

                const binId = y * w + x;

                const count = bin.lineIds.length;

                for (let i = 0; i < count; i++) {
                    rawBinJxx += bin.Jxx[i];
                    rawBinJxy += bin.Jxy[i];
                    rawBinJyy += bin.Jyy[i];
                }
                rawJxx[binId] = rawBinJxx;
                rawJxy[binId] = rawBinJxy;
                rawJyy[binId] = rawBinJyy;
                // ★ 关键修复：使用 bin.density (加权像素穿越总数) 而非 bin.lineIds.length
                // 张量 J 是按像素穿越累加的 (每穿过一个像素 addPassage 一次)，
                // 所以归一化密度也必须用同口径的像素穿越总数，否则 binSize>1 时
                // 归一化后的能量会偏大 ~binSize 倍，导致 coherence 全部饱和到 1。
                rawDensity[binId] = bin.density;
            }
        }

        // 2. Eulerian Phase: Gaussian Smoothing
        const smoothJxx = this.applyGaussianFilter(rawJxx, w, h, this.gaussianKernel);
        const smoothJxy = this.applyGaussianFilter(rawJxy, w, h, this.gaussianKernel);
        const smoothJyy = this.applyGaussianFilter(rawJyy, w, h, this.gaussianKernel);
        const smoothDensity = this.applyGaussianFilter(rawDensity, w, h, this.gaussianKernel);

        // 构建最终向量场
        this.tensorField = Array.from({ length: w }, (_, x) =>
            Array.from({ length: h }, (_, y) => {
                const idx = y * w + x;
                return {
                    xx: smoothJxx[idx],
                    xy: smoothJxy[idx],
                    yy: smoothJyy[idx],
                }
            })
        );
        this.densityField = Array.from({ length: w }, (_, x) =>
            Array.from({ length: h }, (_, y) => rawDensity[y * w + x])
        );

        this.smoothDensityField = Array.from({ length: w }, (_, x) =>
            Array.from({ length: h }, (_, y) => smoothDensity[y * w + x])
        );

        // 3. Lagrangian Phase: Compute Integral Arrays (Prefix Sums)
        // -------------------------------------------------
        this.computeIntegralArrays(binning, lines, LOO);
    }

    /**
     * 计算每条线的能量前缀和数组
     * LOO: 是否启用 Leave-One-Out 机制
     */
    private computeIntegralArrays(binning: Binning, lines: LineData[], LOO = false) {
        const totalLines = lines.length;
        this.lineIntegralEnergies = new Array(totalLines);
        this.lineTraces = new Array(totalLines);

        // Binning 中反向获取每条线的路径 (trace)
        // binning.lineBinSequences[lineId] 存储了该线经过的 { x: number, y: number, angleVec: NormalVec2 } 序列
        const traces = binning.lineBinSequences;

        for (let i = 0; i < totalLines; i++) {
            const trace = traces[i];
            if (!trace || trace.length === 0) {
                this.lineIntegralEnergies[i] = new Float32Array(0);
                this.lineTraces[i] = new Int32Array(0);
                continue;
            }

            const len = trace.length;
            const prefixSum = new Float32Array(len + 1); // +1 为了方便计算 range sum
            const coords = new Int32Array(len * 2);

            prefixSum[0] = 0;

            for (let k = 0; k < len; k++) {
                const { x, y, angleVec } = trace[k];

                // 1. 读取平滑后的张量场 J(x,y)
                const J = this.tensorField[x][y];

                const density = this.smoothDensityField[x][y];

                if (LOO) {
                    const valXX = angleVec.x * angleVec.x;
                    const valXY = angleVec.x * angleVec.y;
                    const valYY = angleVec.y * angleVec.y;

                    // Original Energy = v^T J v
                    const originalEnergy = (valXX * J.xx) + (2 * valXY * J.xy) + (valYY * J.yy);
                    const selfTerms = this.querySmoothedSelfTerms(i, x, y, valXX, valXY, valYY);
                    const looEnergy = Math.max(0, originalEnergy - selfTerms.energy);
                    const adjustedDensity = Math.max(0, density - selfTerms.trace);
                    const normalizedEnergy = adjustedDensity > 1e-5 ? looEnergy / adjustedDensity : 0;

                    prefixSum[k + 1] = prefixSum[k] + normalizedEnergy;

                    coords[k * 2] = x;
                    coords[k * 2 + 1] = y;
                }
                else {
                    // 2. 计算局部一致性能量 Energy = v^T * J * v
                    // v 应该是归一化的切向量
                    const energy = (angleVec.x * angleVec.x * J.xx) + (2 * angleVec.x * angleVec.y * J.xy) + (angleVec.y * angleVec.y * J.yy);

                    const normalizedEnergy = density > 1e-5 ? energy / density : 0;

                    // 3. 累加
                    prefixSum[k + 1] = prefixSum[k] + normalizedEnergy;

                    // 存储坐标以便后续反查
                    coords[k * 2] = x;
                    coords[k * 2 + 1] = y;
                }
            }

            this.lineIntegralEnergies[i] = prefixSum;
            this.lineTraces[i] = coords;
        }
    }

    private cacheLineSelfContributions(
        bins: ReturnType<Binning['getBins']>,
        totalLines: number,
        width: number
    ): SparseLineSelfContributionField[] {
        const height = bins[0]?.length ?? 0;
        const counts = new Int32Array(totalLines);

        for (let y = 0; y < height; y++) {
            for (let x = 0; x < bins.length; x++) {
                const bin = bins[x][y];
                for (let i = 0; i < bin.lineIds.length; i++) {
                    counts[bin.lineIds[i]] += 1;
                }
            }
        }

        const fields = Array.from({ length: totalLines }, (_, lineId) => {
            const size = counts[lineId];
            return {
                binIds: new Int32Array(size),
                xx: new Float32Array(size),
                xy: new Float32Array(size),
                yy: new Float32Array(size),
                trace: new Float32Array(size),
            };
        });
        const offsets = new Int32Array(totalLines);

        for (let y = 0; y < height; y++) {
            for (let x = 0; x < bins.length; x++) {
                const bin = bins[x][y];
                const binId = y * width + x;

                for (let i = 0; i < bin.lineIds.length; i++) {
                    const lineId = bin.lineIds[i];
                    const offset = offsets[lineId];
                    const field = fields[lineId];
                    const xx = bin.Jxx[i];
                    const xy = bin.Jxy[i];
                    const yy = bin.Jyy[i];

                    field.binIds[offset] = binId;
                    field.xx[offset] = xx;
                    field.xy[offset] = xy;
                    field.yy[offset] = yy;
                    field.trace[offset] = xx + yy;

                    offsets[lineId] += 1;
                }
            }
        }

        return fields;
    }

    private findContributionIndex(binIds: Int32Array, targetBinId: number): number {
        let left = 0;
        let right = binIds.length - 1;

        while (left <= right) {
            const mid = (left + right) >> 1;
            const value = binIds[mid];

            if (value === targetBinId) {
                return mid;
            }
            if (value < targetBinId) {
                left = mid + 1;
            } else {
                right = mid - 1;
            }
        }

        return -1;
    }

    private buildKernelSupports(length: number, kernel: Float32Array): KernelSupport[] {
        const supports = new Array<KernelSupport>(length);
        const kernelCenter = Math.floor(kernel.length / 2);

        for (let position = 0; position < length; position++) {
            const indices: number[] = [];
            const weights: number[] = [];
            let weightSum = 0;

            for (let k = 0; k < kernel.length; k++) {
                const offset = k - kernelCenter;
                const index = position + offset;
                if (index < 0 || index >= length) continue;

                const weight = kernel[k];
                indices.push(index);
                weights.push(weight);
                weightSum += weight;
            }

            const normalization = weightSum || 1;
            supports[position] = {
                indices: Int32Array.from(indices),
                weights: Float32Array.from(weights.map((weight) => weight / normalization)),
            };
        }

        return supports;
    }

    private querySmoothedSelfTerms(
        lineId: number,
        x: number,
        y: number,
        valXX: number,
        valXY: number,
        valYY: number
    ): { energy: number; trace: number } {
        const lineField = this.lineSelfContributionFields[lineId];
        if (!lineField || lineField.binIds.length === 0) {
            return { energy: 0, trace: 0 };
        }

        const xSupport = this.xKernelSupports[x];
        const ySupport = this.yKernelSupports[y];
        let energy = 0;
        let trace = 0;

        for (let xi = 0; xi < xSupport.indices.length; xi++) {
            const px = xSupport.indices[xi];
            const wx = xSupport.weights[xi];

            for (let yi = 0; yi < ySupport.indices.length; yi++) {
                const py = ySupport.indices[yi];
                const contributionIndex = this.findContributionIndex(lineField.binIds, py * this.width + px);
                if (contributionIndex === -1) continue;

                const weight = wx * ySupport.weights[yi];
                energy += weight * (
                    (valXX * lineField.xx[contributionIndex]) +
                    (2 * valXY * lineField.xy[contributionIndex]) +
                    (valYY * lineField.yy[contributionIndex])
                );
                trace += weight * lineField.trace[contributionIndex];
            }
        }

        return { energy, trace };
    }

    /**
     * 计算每条线的重要性（lineFinalScore），即前缀和数组的最后一个值除以线的长度
     * 
     */
    public computeLineImportances(): Float32Array {
        const totalLines = this.lineIntegralEnergies.length;
        const importances = new Float32Array(totalLines);

        for (let i = 0; i < totalLines; i++) {
            const prefixSum = this.lineIntegralEnergies[i];
            if (prefixSum.length === 0) {
                importances[i] = 0;
            } else {
                importances[i] = prefixSum[prefixSum.length - 1] / (prefixSum.length - 1); // 最后一个值除以线的长度
            }
        }

        return importances;
    }

    public computeLineWindowScores(w: number): Float32Array {
        const totalLines = this.lineIntegralEnergies.length;
        const scores = new Float32Array(totalLines);
        const halfW = Math.floor(w / 2);

        for (let i = 0; i < totalLines; i++) {
            const prefixSum = this.lineIntegralEnergies[i];
            const len = prefixSum.length - 1;

            if (len <= 0) {
                scores[i] = 0;
                continue;
            }

            let accumulatedWindowScore = 0;

            for (let t = 0; t < len; t++) {
                const start = Math.max(0, t - halfW);
                const end = Math.min(len, t + halfW + 1);
                const actualW = end - start;

                if (actualW <= 0) {
                    continue;
                }

                const rangeEnergy = prefixSum[end] - prefixSum[start];
                accumulatedWindowScore += rangeEnergy / actualW;
            }

            scores[i] = accumulatedWindowScore / len;
        }

        return scores;
    }

    public computeLineLocalScores(): Float32Array {
        return this.computeLineWindowScores(1);
    }

    public computeLineMeanField(field: number[][]): Float32Array {
        const totalLines = this.lineTraces.length;
        const means = new Float32Array(totalLines);

        for (let i = 0; i < totalLines; i++) {
            const coords = this.lineTraces[i];
            const len = coords.length / 2;

            if (len <= 0) {
                means[i] = 0;
                continue;
            }

            let sum = 0;
            for (let t = 0; t < len; t++) {
                const x = coords[t * 2];
                const y = coords[t * 2 + 1];
                sum += field[x][y];
            }

            means[i] = sum / len;
        }

        return means;
    }

    public computeLineFieldProfileStats(field: number[][], highThreshold: number): LineFieldProfileStats[] {
        const totalLines = this.lineTraces.length;
        const stats: LineFieldProfileStats[] = [];

        for (let i = 0; i < totalLines; i++) {
            const coords = this.lineTraces[i];
            const len = coords.length / 2;

            if (len <= 0) {
                stats.push({ mean: 0, variance: 0, std: 0, highCoverage: 0 });
                continue;
            }

            let sum = 0;
            let sumSquares = 0;
            let highCount = 0;

            for (let t = 0; t < len; t++) {
                const x = coords[t * 2];
                const y = coords[t * 2 + 1];
                const value = field[x][y];
                sum += value;
                sumSquares += value * value;
                if (value > highThreshold) {
                    highCount += 1;
                }
            }

            const meanValue = sum / len;
            const variance = Math.max(0, sumSquares / len - meanValue * meanValue);
            stats.push({
                mean: meanValue,
                variance,
                std: Math.sqrt(variance),
                highCoverage: highCount / len,
            });
        }

        return stats;
    }

    public computeLineMeanMessiness(w: number): Float32Array {
        const { coherenceScores } = this.query(w);
        return this.computeLineMeanField(coherenceScores);
    }

    public computeLineMessinessProfileStats(w: number, highThreshold: number): LineFieldProfileStats[] {
        const { coherenceScores } = this.query(w);
        return this.computeLineFieldProfileStats(coherenceScores, highThreshold);
    }

    public computeLineMeanLocalMessiness(): Float32Array {
        return this.computeLineMeanMessiness(1);
    }

    public computeLineMeanSmoothedDensity(): Float32Array {
        const totalLines = this.lineTraces.length;
        const densityMeans = new Float32Array(totalLines);

        for (let i = 0; i < totalLines; i++) {
            const coords = this.lineTraces[i];
            const len = coords.length / 2;

            if (len <= 0) {
                densityMeans[i] = 0;
                continue;
            }

            let densitySum = 0;
            for (let t = 0; t < len; t++) {
                const x = coords[t * 2];
                const y = coords[t * 2 + 1];
                densitySum += this.smoothDensityField[x][y];
            }

            densityMeans[i] = densitySum / len;
        }

        return densityMeans;
    }

    public getMaxTraceLength(): number {
        let maxLength = 0;
        for (const coords of this.lineTraces) {
            const length = coords.length / 2;
            if (length > maxLength) {
                maxLength = length;
            }
        }
        return maxLength;
    }

    public getTensorField(): Tensor2x2[][] {
        return this.tensorField;
    }

    public getSmoothDensityField(): number[][] {
        return this.smoothDensityField;
    }

    /**
     * 交互式查询：根据窗口 w 计算 coherenceScores
     * 复杂度：O(TotalPoints)，非常快，适合在 render loop 中调用
     * @param w 滑动窗口大小 (path length in pixels)
     */
    public query(w: number): StructureTensorScanResult {
        const width = this.width;
        const height = this.height;

        // 1. 初始化结果网格
        // accumScore: 累加经过该 Bin 的所有线的“窗口一致性得分”
        // accumCount: 累加经过该 Bin 的所有线 (用于平均)
        const accumScore = new Float32Array(width * height).fill(0);
        const accumCount = new Float32Array(width * height).fill(0);

        // 2. 遍历每条线，利用前缀和 O(1) 计算窗口得分
        for (let i = 0; i < this.lineIntegralEnergies.length; i++) {
            const prefixSum = this.lineIntegralEnergies[i];
            const coords = this.lineTraces[i];
            const len = prefixSum.length - 1; // 实际点数

            if (len === 0) continue;

            // 沿着线走
            for (let t = 0; t < len; t++) {
                // Bin 坐标
                const x = coords[t * 2];
                const y = coords[t * 2 + 1];
                const binIdx = y * width + x;

                // --- 核心逻辑：O(1) 滑动窗口积分 ---

                // 定义窗口范围 [start, end]
                // 我们想看以 t 为中心，或者以 t 为结尾的窗口？
                // 论文中通常是以 t 为中心 (Center) 或 t 为前瞻 (Look-ahead)。
                // 这里用 Center window: [t - w/2, t + w/2]

                const halfW = Math.floor(w / 2);
                const start = Math.max(0, t - halfW);
                const end = Math.min(len, t + halfW + 1); // exclusive for prefix sum logic

                // 实际窗口长度 (处理边界)
                const actualW = end - start;

                if (actualW === 0) continue;

                // Range Sum = P[end] - P[start]
                const rangeEnergy = prefixSum[end] - prefixSum[start];

                // Average Energy within window
                const avgEnergy = rangeEnergy / actualW;

                // 累加到 Bin
                accumScore[binIdx] += avgEnergy;
                accumCount[binIdx] += 1;
            }
        }

        // 3. 生成最终的 coherenceScores 矩阵
        const coherenceScores: number[][] = [];
        const densityScores: number[][] = [];
        const sampleCounts: number[][] = [];

        // 需要归一化因子吗？
        // Energy 本质上是 v^T J v。如果 J 很大（高密度区），Energy 就很大。
        // 我们通常希望衡量的是 "一致性比例" 而不是 "绝对能量"。
        // 修正：在上面的 computeIntegralArrays 中，J 来源于 density-weighted accumulation。
        // 为了得到 normalized anisotropy score, 我们应该除以局部密度。

        for (let x = 0; x < width; x++) {
            const mRow: number[] = [];
            const dRow: number[] = [];
            const cRow: number[] = [];
            for (let y = 0; y < height; y++) {
                const idx = y * width + x;
                const count = accumCount[idx];
                const rho = this.densityField[x][y]; // 平滑后的密度

                dRow.push(rho);
                cRow.push(count);

                if (count === 0 || rho < 1e-5) {
                    mRow.push(0); // 无数据 -> 视为背景
                    continue;
                }

                // Raw Score 是 Sum(Energy) / Count
                const rawScore = accumScore[idx] / count;

                // Normalize by Density?
                // 因为 J ~ Density * Orientation_Consistency
                // 所以 Energy ~ Density
                // 我们想要的是 Consistency ~ Energy / Density
                // 注意：这里用 rho (smooth density) 可能比 bin count 更稳定

                let consistency = rawScore; // 防止除0

                // 裁剪到 [0, 1] 区间
                consistency = Math.max(0, Math.min(1, consistency));

                // 转换：Consistency (1.0) -> Messiness (0.0)
                //      Consistency (0.0) -> Messiness (1.0)
                mRow.push(1.0 - consistency);
            }
            coherenceScores.push(mRow);
            densityScores.push(dRow);
            sampleCounts.push(cRow);
        }

        return { coherenceScores, densityScores, sampleCounts };
    }

    // --- Helper: Simple Gaussian Blur for 1D Array representing 2D Grid ---
    private applyGaussianFilter(data: Float32Array, w: number, h: number, kernel: Float32Array): Float32Array {
        // 简单实现：由于是 separable filter，先做 X 方向，再做 Y 方向
        // 实际工程中可用 WebGL Shader 或更优化的库
        if (kernel.length === 1 && kernel[0] === 1) return new Float32Array(data);

        const kLen = kernel.length;
        const kCenter = Math.floor(kLen / 2);

        const temp = new Float32Array(data.length);
        const result = new Float32Array(data.length);

        // Pass 1: X direction
        for (let y = 0; y < h; y++) {
            for (let x = 0; x < w; x++) {
                let sum = 0;
                let weightSum = 0;
                for (let k = 0; k < kLen; k++) {
                    const offset = k - kCenter;
                    const px = x + offset;
                    if (px >= 0 && px < w) {
                        const val = data[y * w + px];
                        sum += val * kernel[k];
                        weightSum += kernel[k];
                    }
                }
                temp[y * w + x] = sum / (weightSum || 1);
            }
        }

        // Pass 2: Y direction
        for (let x = 0; x < w; x++) {
            for (let y = 0; y < h; y++) {
                let sum = 0;
                let weightSum = 0;
                for (let k = 0; k < kLen; k++) {
                    const offset = k - kCenter;
                    const py = y + offset;
                    if (py >= 0 && py < h) {
                        const val = temp[py * w + x];
                        sum += val * kernel[k];
                        weightSum += kernel[k];
                    }
                }
                result[y * w + x] = sum / (weightSum || 1);
            }
        }

        return result;
    }

    private makeGaussianKernel(sigma: number): number[] {
        const size = Math.ceil(sigma * 3) * 2 + 1; // 3-sigma rule
        const kernel = [];
        const center = Math.floor(size / 2);
        let sum = 0;
        for (let i = 0; i < size; i++) {
            const x = i - center;
            const g = Math.exp(-(x * x) / (2 * sigma * sigma));
            kernel.push(g);
            sum += g;
        }
        // Normalize
        return kernel.map(v => v / sum);
    }

}
